import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const mainNav = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/电影' },
  { label: '电视剧', to: '/category/电视剧' },
  { label: '动漫', to: '/category/动漫' },
  { label: '综艺', to: '/category/综艺' },
  { label: '纪录片', to: '/category/纪录片' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    const params = new URLSearchParams(location.search);
    setKeyword(location.pathname === '/search' ? params.get('q') ?? '' : '');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== '/search') return;
    const timer = window.setTimeout(() => {
      const value = keyword.trim();
      const current = new URLSearchParams(location.search).get('q') ?? '';
      if (value && value !== current) navigate(`/search?q=${encodeURIComponent(value)}`, { replace: true });
    }, 550);
    return () => window.clearTimeout(timer);
  }, [keyword, location.pathname, location.search, navigate]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword('');
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-background-950 text-background-100/80">
        <div className="mx-auto flex h-8 max-w-[1400px] items-center justify-between px-4 text-[11px] tracking-wide md:px-6">
          <p className="truncate">本站为影视资讯与推荐平台，不提供在线播放与下载</p>
          <Link to="/favorites" className="hidden items-center gap-1 whitespace-nowrap text-background-100/80 transition hover:text-accent-400 sm:flex">
            <i className="ri-heart-3-line w-3.5 h-3.5 flex items-center justify-center" />
            我的收藏
          </Link>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-6 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-film-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">菠萝蜜网</span>
              <span className="mt-1 text-[10px] tracking-[0.18em] text-foreground-500">BOLUOMI CINEMA</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {mainNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm transition ${
                    isActive
                      ? 'bg-primary-100 font-medium text-primary-800'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="ml-auto hidden items-center md:flex">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 transition focus-within:border-primary-400 focus-within:bg-background-50">
              <i className="ri-search-line w-4 h-4 flex items-center justify-center text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、演员、导演"
                className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
              />
              <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition hover:bg-primary-600">
                搜索
              </button>
            </div>
          </form>

          <Link
            to="/favorites"
            className="ml-auto hidden items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-800 transition hover:bg-secondary-100 md:flex lg:ml-0"
          >
            <i className="ri-heart-3-line w-4 h-4 flex items-center justify-center" />
            收藏夹
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="打开导航菜单"
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="animate-fade-in border-b border-background-200 bg-background-50 shadow-sm lg:hidden">
          <div className="mx-auto max-w-[1400px] px-4 py-4 md:px-6">
            <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line w-4 h-4 flex items-center justify-center text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、演员、导演"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </form>
            <div className="grid grid-cols-2 gap-2">
              {mainNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm ${isActive ? 'bg-primary-100 font-medium text-primary-800' : 'bg-background-100 text-foreground-700'}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <NavLink to="/favorites" className="rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                收藏夹
              </NavLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}